import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FiEdit2, FiX } from "react-icons/fi";
import { z } from "zod";

import { Button } from "~/components/Button";
import { Modal } from "~/components/Modal";
import { TextInput } from "~/components/TextInput";
import { updatePlanInstallments } from "~/modules/sales/services";
import type { PaymentPlan } from "~/modules/sales/types";
import { insertToast } from "~/modules/toasts/services";

type Props = {
	plan: PaymentPlan;
	onUpdate: (plan: PaymentPlan) => void;
};

const schema = z.object({
	installments: z.coerce
		.number({
			required_error: "Obrigatório",
			invalid_type_error: "Deve ser um número",
		})
		.min(1, "O número mínimo é 1")
		.max(12, "O número máximo é 12"),
});

type FormData = z.infer<typeof schema>;

export const PlanDetailsInstallments: React.FC<Props> = ({
	plan,
	onUpdate,
}) => {
	const [showModal, setShowModal] = useState(false);
	const [loading, setLoading] = useState(false);

	const { control, handleSubmit, formState, reset } = useForm<FormData>({
		resolver: zodResolver(schema),
		defaultValues: {
			installments: plan.installments || 1,
		},
	});

	const handleClose = useCallback(() => {
		setShowModal(false);
		reset({ installments: plan.installments || 1 });
	}, [plan.installments, reset]);

	const onSubmit = useCallback(
		async (data: FormData) => {
			setLoading(true);
			try {
				const updated = await updatePlanInstallments(
					plan.id,
					data.installments,
				);
				onUpdate(updated);
				setShowModal(false);
				insertToast({
					type: "success",
					message: "Parcelas atualizadas com sucesso!",
				});
			} catch (err) {
				// error already handled by service
			} finally {
				setLoading(false);
			}
		},
		[plan.id, onUpdate],
	);

	return (
		<div className="flex flex-col gap-2">
			<p>Número máximo de parcelas (Cartão de Crédito):</p>
			<div className="flex items-center gap-4">
				<span className="font-semibold">{plan.installments || 1}x</span>
				<button
					type="button"
					onClick={() => setShowModal(true)}
					className="p-1.5 text-gpa-blue-500 hover:bg-gpa-blue-50 rounded transition-colors"
				>
					<FiEdit2 />
				</button>
			</div>

			<Modal visible={showModal}>
				<div className="flex flex-col min-w-[300px] md:min-w-[400px]">
					<div className="flex items-center justify-between mb-4">
						<h1 className="text-xl">Editar Parcelas</h1>
						<button
							type="button"
							className="p-2 rounded-full hover:bg-slate-50"
							onClick={handleClose}
						>
							<FiX size={26} />
						</button>
					</div>
					<form
						onSubmit={handleSubmit(onSubmit)}
						className="flex flex-col gap-4"
					>
						<Controller
							control={control}
							name="installments"
							render={({ field }) => (
								<TextInput
									type="number"
									label="Número de parcelas"
									value={field.value}
									onChange={field.onChange}
									error={formState.errors.installments?.message}
								/>
							)}
						/>
						<Button type="submit" loading={loading}>
							Salvar
						</Button>
					</form>
				</div>
			</Modal>
		</div>
	);
};
